import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bank, Classifier } from "../../models/interfaces";
import { ClassifierContext } from "../../context/classifier/ClassifierContext";
import Message from "../../components/ui/Message";

interface LocationState {
    classifier: Classifier;
}

export const ClassifierFiPage = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { classifier } = location.state as LocationState;

    const {
        getFIs,
        getFIByClassifier,
        assignClassifierToFi,
        unassignClassifierFromFi
    } = useContext(ClassifierContext);

    const [fis, setFis] = useState<Bank[]>([]);
    const [selectedFis, setSelectedFis] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState<number | null>(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    /* =========================
       Cargar instituciones
    ========================== */

    useEffect(() => {

        setLoading(true);

        Promise.all([
            getFIs(),
            getFIByClassifier(classifier.classifierId!)
        ])
            .then(([allFis, classifierFis]) => {

                setFis(allFis || []);
                setSelectedFis(classifierFis.map((f: any) => f.id));

            })
            .finally(() => setLoading(false));

    }, []);

    /* =========================
       Toggle FI
    ========================== */

    const handleToggleFi = async (fi: number) => {

        const isSelected = selectedFis.includes(fi);

        setUpdating(fi);

        let resp;

        if (isSelected) {
            resp = await unassignClassifierFromFi(classifier.classifierId!, fi);
        } else {
            resp = await assignClassifierToFi(classifier.classifierId!, fi);
        }

        setUpdating(null);

        if (resp === "success") {

            setSelectedFis(prev =>
                isSelected
                    ? prev.filter(id => id !== fi)
                    : [...prev, fi]
            );

        } else {

            setErrorMessage(resp);

        }

    };

    const removeError = () => setErrorMessage("");

    /* =========================
       Render
    ========================== */

    return (
        <div className="mt-5 container">

            {/* Header */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <h5>
                    Instituciones financieras del clasificador:{" "}
                    <span className="fw-bold">
                        {classifier.name}
                    </span>
                </h5>

            </div>

            {/* Tabla */}

            {loading ? (

                <div className="text-center mt-4">
                    <span className="spinner-border" />
                </div>

            ) : (

                <table className="table table-hover table-striped">

                    <thead className="thead-dark">
                        <tr>
                            <th style={{ width: 120, textAlign: "center" }}>
                                Asignado
                            </th>
                            <th>Código</th>
                            <th>Institución financiera</th>
                        </tr>
                    </thead>

                    <tbody>

                        {fis.length > 0 ? (

                            fis.map(fi => (

                                <tr key={fi.id}>

                                    <td className="text-center">

                                        <input
                                            type="checkbox"
                                            disabled={updating === fi.id}
                                            checked={selectedFis.includes(fi.id!)}
                                            onChange={() => handleToggleFi(fi.id!)}
                                        />

                                    </td>

                                    <td>{fi.code}</td>

                                    <td>{fi.name.toUpperCase()}</td>

                                </tr>

                            ))

                        ) : (

                            <tr>
                                <td colSpan={3} className="text-center">
                                    No existen instituciones financieras.
                                </td>
                            </tr>

                        )}

                    </tbody>

                </table>

            )}

            {/* Acciones */}

            <div className="d-flex justify-content-center mt-4">

                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                >
                    Volver
                </button>

            </div>

            {/* Mensajes */}

            {errorMessage && (

                <Message
                    type="error"
                    message={errorMessage}
                    closeDialog={removeError}
                    handleOk={removeError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />

            )}

        </div>
    );
};